/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   LicenseUnverifiablePage.tsx
 *     Feature         :   286 - FCM licence enforcement and estate limits (US4, FR-011)
 *     Description     :   Rendered INSTEAD of the app shell when the licence could not be read. Never
 *                         the subscription notice: a failed read is not a suspension (FR-011). Offers a
 *                         retry that re-reads the session (LicenseGate) and sign-out.
 *                         Kit + tokens + lucide-react only (Constitution XLV).
 *     Author          :   Peter Dobler
 * =========================================================================================
 */
import React from 'react';
import { CloudOff, RefreshCw, LogOut } from 'lucide-react';
import { Card, Heading, Text, Button } from '@/components/ui';

export interface LicenseUnverifiablePageProps {
  onRetry: () => void;
  retrying: boolean;
  onSignOut: () => void;
}

export const LicenseUnverifiablePage: React.FC<LicenseUnverifiablePageProps> = ({ onRetry, retrying, onSignOut }) => (
  <main className="flex min-h-screen items-center justify-center bg-[var(--color-bg-page)] px-4">
    <Card padding="lg" className="w-full max-w-lg text-center">
      <CloudOff aria-hidden className="mx-auto mb-4 h-10 w-10 text-[var(--color-text-secondary)]" />
      <Heading as="h1" level="h4">Can’t verify your subscription right now</Heading>
      <Text className="mt-3">
        We could not check your subscription. This is usually temporary — try again in a moment.
      </Text>
      <div className="mt-6 flex flex-wrap justify-center gap-3">
        <Button leftIcon={<RefreshCw className="h-4 w-4" />} onClick={onRetry} disabled={retrying}>
          {retrying ? 'Checking…' : 'Try again'}
        </Button>
        <Button variant="secondary" leftIcon={<LogOut className="h-4 w-4" />} onClick={onSignOut}>
          Sign out
        </Button>
      </div>
    </Card>
  </main>
);

export default LicenseUnverifiablePage;
